import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useEmployeeDashboard } from '../../../hooks/useDashboard';
import { projectRequestsApi, ProjectRequest } from '../../../api/projectRequestsApi';
import { Card } from '../../../components/common/Card';
import { Icons } from '../../../components/common/Icons';
import { getGreeting, formatDate, getFirstName } from '../../../utils/helpers';
import { STATUS_COLORS } from '../../../utils/constants';
import { ProjectRequestModal } from './ProjectRequestModal';
import './EmployeeDashboard.css';
import './ProjectRequestModal.css';

export function EmployeeDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useEmployeeDashboard();
  
  // Project requests state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [myRequests, setMyRequests] = useState<ProjectRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Fetch project requests
  const fetchMyRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await projectRequestsApi.getMyRequests();
      setMyRequests(response.requests);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const handleRequestSuccess = () => {
    fetchMyRequests();
    refetch();
  };

  if (error) {
    return (
      <div className="dashboard-error">
        <Icons.AlertCircle />
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={refetch}>Try Again</button>
      </div>
    );
  }

  if (!data || !user) {
    // Render basic structure even without data for instant page load
    return (
      <div className="employee-dashboard">
        <header className="dashboard-header-content">
          <div className="header-greeting">
            <h1>{getGreeting()}, {user?.name ? getFirstName(user.name) : 'there'}!</h1>
            <p>Track your time and manage your timesheets.</p>
          </div>
        </header>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Billable Hours',
      value: data.billableHoursThisMonth ?? 0,
      icon: <Icons.Clock />,
      color: '#10b981',
      link: '/timesheets',
      subtitle: 'This Month'
    },
    {
      title: 'Non-Billable Hours',
      value: data.nonBillableHoursThisMonth ?? 0,
      icon: <Icons.Clock />,
      color: '#6366f1',
      link: '/timesheets',
      subtitle: 'This Month'
    },
    {
      title: 'Days Missing',
      value: data.daysMissing,
      icon: <Icons.AlertCircle />,
      color: data.daysMissing > 0 ? '#ef4444' : '#10b981',
      link: '/log-time',
    },
    {
      title: 'Pending',
      value: data.pendingTimesheets,
      icon: <Icons.History />,
      color: '#f59e0b',
      link: '/timesheets',
      state: { status: 'submitted' }
    },
    {
      title: 'Approved',
      value: data.approvedTimesheets,
      icon: <Icons.CheckCircle />,
      color: '#8b5cf6',
      link: '/timesheets',
      state: { status: 'approved' }
    },
  ];

  return (
    <div className="employee-dashboard">
      <header className="dashboard-header-content">
        <div className="header-greeting">
          <h1>{getGreeting()}, {getFirstName(user.name)}!</h1>
          <p>Track your time and manage your timesheets.</p>
        </div>
        <div className="header-actions">
          <button 
            className="request-project-btn"
            onClick={() => setShowRequestModal(true)}
          >
            <Icons.Plus />
            Request Project
          </button>
          <button 
            className="add-time-btn"
            onClick={() => navigate('/log-time')}
          >
            <Icons.Clock />
            Fill Timesheet
          </button>
        </div>
      </header>

      <section className="stats-grid">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" padding="none" hoverable
            onClick={() => navigate(stat.link, { state: stat.state })}
          >
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-details">
                <span className="stat-title">{stat.title}</span>
                <span className="stat-value">{stat.value}{'subtitle' in stat && 'h'}</span>
                {'subtitle' in stat && <span className="stat-subtitle">{stat.subtitle}</span>}
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* ── Work Request Banner ── */}
      {data.assignedProjects.length === 0 && !data.hasPendingWorkRequest && (
        <Card className="emp-wr-banner" padding="none" hoverable onClick={() => navigate('/work-requests')}>
          <div className="emp-wr-banner-inner">
            <div className="emp-wr-banner-icon"><Icons.Hand /></div>
            <div className="emp-wr-banner-text">
              <strong>No active projects.</strong> Let your manager know you're available for work.
            </div>
            <button className="emp-wr-btn" onClick={(e) => { e.stopPropagation(); navigate('/work-requests'); }}>
              Request Work <Icons.ArrowRight />
            </button>
          </div>
        </Card>
      )}
      {data.hasPendingWorkRequest && (
        <Card className="emp-wr-banner emp-wr-banner-pending" padding="none" hoverable onClick={() => navigate('/work-requests')}>
          <div className="emp-wr-banner-inner">
            <div className="emp-wr-banner-icon"><Icons.Check /></div>
            <div className="emp-wr-banner-text">
              <strong>Work request submitted.</strong> Your manager/PMO will assign you to a project soon.
            </div>
          </div>
        </Card>
      )}

      <div className="dashboard-grid">
        <Card title="My Projects" className="projects-card">
          <div className="projects-list">
            {data.assignedProjects.map((project) => (
              <div key={project.id} className="project-item">
                <div className="project-icon">
                  <Icons.Folder />
                </div>
                <div className="project-info">
                  <span className="project-name">{project.name}</span>
                  <span className="project-code">{project.code}</span>
                </div>
                <span 
                  className="project-status"
                  style={{ 
                    backgroundColor: `${STATUS_COLORS[project.status]}15`,
                    color: STATUS_COLORS[project.status]
                  }}
                >
                  {project.status}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Weekly Overview" className="overview-card">
          <div className="week-progress">
            <div className="progress-ring">
              <svg viewBox="0 0 100 100" className="progress-svg">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={`${(data.hoursThisWeek / 40) * 251.2} 251.2`}
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="progress-center">
                <span className="progress-hours">{data.hoursThisWeek}</span>
                <span className="progress-label">of 40h</span>
              </div>
            </div>
            <div className="progress-legend">
              <div className="legend-item">
                <span className="legend-dot filled" />
                <span>Logged</span>
                <span className="legend-value">{data.hoursThisWeek}h</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot empty" />
                <span>Remaining</span>
                <span className="legend-value">{Math.max(0, 40 - data.hoursThisWeek)}h</span>
              </div>
            </div>
          </div>
        </Card>

        <Card title={`Period: ${formatDate(data.periodStart)} - ${formatDate(data.periodEnd)}`} className="entries-card">
          <div className="monthly-chart">
            <div className="chart-header">
              <span className="chart-title">Daily Hours by Project</span>
              <span className="chart-total">Total: {data.hoursThisPeriod}h</span>
            </div>
            <div className="chart-container">
              <div className="chart-bars">
                {data.chartData.map((day) => {
                  const maxHours = 8;
                  const dayDate = new Date(day.date);
                  const dayNum = dayDate.getDate();
                  const isToday = day.date === new Date().toISOString().split('T')[0];
                  const isMissing = day.totalHours === 0 && !day.isWeekend && !day.isHoliday && !isToday;
                  
                  // Build tooltip
                  const tooltipParts = [`${formatDate(day.date)}: ${day.totalHours}h`];
                  if (day.isHoliday && day.holidayName) {
                    tooltipParts.push(`🎉 Holiday: ${day.holidayName}`);
                  }
                  day.projects.forEach(p => {
                    tooltipParts.push(`  • ${p.projectName}: ${p.hours}h`);
                  });
                  
                  return (
                    <div 
                      key={day.date} 
                      className={`chart-bar-wrapper ${day.isWeekend ? 'weekend' : ''} ${day.isHoliday ? 'holiday' : ''} ${isToday ? 'today' : ''}`}
                      title={tooltipParts.join('\n')}
                    >
                      <div className="chart-bar-container">
                        {isMissing ? (
                          <div className="chart-bar empty" style={{ height: '4px' }} />
                        ) : (
                          <div className="chart-bar-stack">
                            {day.projects.map((project, pIdx) => {
                              const heightPercent = (project.hours / maxHours) * 100;
                              return (
                                <div
                                  key={`${day.date}-${project.projectId}`}
                                  className="chart-bar-segment"
                                  style={{
                                    height: `${heightPercent}%`,
                                    backgroundColor: project.color,
                                  }}
                                  title={`${project.projectName}: ${project.hours}h`}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <span className="chart-bar-label">{dayNum}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Project Legend */}
            <div className="chart-project-legend">
              {data.assignedProjects.slice(0, 6).map((project, idx) => {
                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                return (
                  <div key={project.id} className="legend-item">
                    <span 
                      className="legend-color-box" 
                      style={{ backgroundColor: colors[idx % colors.length] }}
                    />
                    <span className="legend-project-name">{project.name}</span>
                  </div>
                );
              })}
              <div className="legend-item">
                <span className="legend-color-box" style={{ backgroundColor: 'var(--accent-primary)', opacity: 0.5 }} />
                <span className="legend-project-name">Holiday</span>
              </div>
              <div className="legend-item">
                <span className="legend-color-box missing" />
                <span className="legend-project-name">Missing</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Project Requests Section */}
      {myRequests.length > 0 && (
        <Card title="My Project Requests" className="requests-card">
          <div className="requests-header-row">
            <span className="requests-header-label">Project Name</span>
            <span className="requests-header-label">Status</span>
          </div>
          <div className="requests-list">
            {myRequests.slice(0, 5).map((request) => (
              <div key={request.id} className="request-item">
                <div className="request-info">
                  <span className="request-project">{request.project_name}</span>
                  <span className="request-date">
                    Requested on {formatDate(request.created_at)}
                  </span>
                </div>
                <span
                  className="request-status"
                  style={{
                    backgroundColor: `${STATUS_COLORS[request.status] || STATUS_COLORS.draft}15`,
                    color: STATUS_COLORS[request.status] || STATUS_COLORS.draft
                  }}
                >
                  {request.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Project Request Modal */}
      <ProjectRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSuccess={handleRequestSuccess}
      />
    </div>
  );
}

export default EmployeeDashboard;
